import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EmptyView } from '@shared/components/ui/EmptyView';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown/FiltersDropdown';
import { Search } from '@shared/components/ui/Search/Search';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBatchesMain } from '../../hooks/useBatchesMain';
import { BatchesTable } from '../display/BatchesTable';
import { CardList } from '../display/CardList';
import { ErrorState } from '../feedback/ErrorState';
import { LoadingState } from '../feedback/LoadingState';
import { BatchFiltersPanel } from '../interaction/BatchFiltersPanel';
import { StatusTabs } from '../interaction/StatusTabs';
import BatchesModals, { type BatchesModalsRef } from '../modals/BatchesModals';

export const Main = () => {
  const { t } = useTranslation('batchesModule');
  const modalsRef = useRef<BatchesModalsRef>(null);

  const {
    batches,
    isLoading,
    isFetching,
    error,
    total,
    globalTotal,
    statusCounts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    searchTerm,
    activeTab,
    sorting,
    filterConditions,
    activeFiltersCount,
    hasActiveFilters,
    handleSearch,
    handleTabChange,
    handleFilterChange,
    handleSortingChange,
  } = useBatchesMain();

  const handleCreate = useCallback(() => {
    modalsRef.current?.openCreate();
  }, []);

  const breadcrumbs = useMemo(() => [{ label: t('title'), path: '/batches' }], [t]);

  const headerActions = useMemo(
    () => [
      {
        id: 'create',
        label: t('actions.create'),
        onClick: handleCreate,
        variant: 'primary' as const,
      },
    ],
    [handleCreate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Search
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onEnter={handleSearch}
              dataTestId="batches-search"
            />
            <FiltersDropdown
              title={t('filters.title')}
              activeFiltersCount={activeFiltersCount}
              triggerTestId="batches-filters-toggle"
            >
              <BatchFiltersPanel conditions={filterConditions} onChange={handleFilterChange} />
            </FiltersDropdown>
          </div>
          <TotalResults total={globalTotal} />
        </div>

        <StatusTabs activeTab={activeTab} onTabChange={handleTabChange} counts={statusCounts} allTotal={globalTotal} />
      </div>

      {batches.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          title={hasActiveFilters ? undefined : t('empty.title')}
          description={hasActiveFilters ? undefined : t('empty.description')}
        />
      )}

      {total > 0 && (
        <div className="mb-4 mt-4">
          <div className="hidden md:block">
            <BatchesTable
              batches={batches}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              modalsRef={modalsRef}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          </div>
          <div
            className={`block md:hidden transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <CardList
              batches={batches}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              modalsRef={modalsRef}
            />
          </div>
        </div>
      )}

      <BatchesModals ref={modalsRef} />
    </div>
  );
};
